import type { DragEvent } from 'react';
import { cn } from '../../../lib/cn';
import { PASSI, type StepKind } from '../../../types/automazioni.types';
import { ASPETTO_PASSI, NOME_CORTO, ORDINE_PASSI, TIPO_TRASCINATO } from './aspettoPassi';

interface TavolozzaPassiProps {
  onAggiungi: (kind: StepKind) => void;
}

/**
 * I tasti per aggiungere caselle, a lato della lavagna.
 *
 * Ogni tasto ha il colore e l'icona della casella che crea: chi lo preme
 * ritrova subito la stessa cosa nel disegno. Si clicca oppure si trascina
 * nel punto preciso in cui la si vuole.
 */
export function TavolozzaPassi({ onAggiungi }: TavolozzaPassiProps) {
  const iniziaTrascinamento = (evento: DragEvent<HTMLButtonElement>, kind: StepKind) => {
    evento.dataTransfer.setData(TIPO_TRASCINATO, kind);
    evento.dataTransfer.effectAllowed = 'move';
  };

  return (
    <div className="bg-white border border-neutral-200 shadow-[0_2px_8px_rgba(0,0,0,0.06)] p-2">
      <p className="text-[9.5px] font-semibold uppercase tracking-[0.12em] text-neutral-400 text-center pb-2">
        Aggiungi
      </p>

      <div className="flex flex-col gap-1.5">
        {ORDINE_PASSI.map((kind) => {
          const aspetto = ASPETTO_PASSI[kind];
          const Icona = aspetto.icona;
          return (
            <div key={kind} className="group relative">
              <button
                type="button"
                draggable
                onDragStart={(e) => iniziaTrascinamento(e, kind)}
                onClick={() => onAggiungi(kind)}
                aria-label={PASSI[kind].label}
                className={cn(
                  'w-[64px] h-[64px] flex flex-col items-center justify-center gap-1.5 cursor-grab active:cursor-grabbing',
                  'shadow-[inset_0_-2px_0_rgba(0,0,0,0.15)] transition-[transform,background-color,box-shadow] duration-150',
                  'hover:-translate-y-px hover:shadow-[inset_0_-2px_0_rgba(0,0,0,0.15),0_4px_10px_rgba(0,0,0,0.12)] active:translate-y-0 active:scale-[0.97]',
                  'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-offset-2 focus-visible:ring-neutral-900',
                  aspetto.pieno,
                  aspetto.pienoHover
                )}
              >
                <Icona className="w-5 h-5" strokeWidth={2.25} />
                <span className="text-[10.5px] font-semibold leading-none">{NOME_CORTO[kind]}</span>
              </button>

              {/* Il nome intero e cosa fa, quando ci passi sopra. */}
              <div className="pointer-events-none absolute left-full top-1/2 -translate-y-1/2 ml-3 w-56 opacity-0 translate-x-1 group-hover:opacity-100 group-hover:translate-x-0 transition-[opacity,transform] duration-150 z-10">
                <div className="bg-neutral-900 text-white px-3 py-2 shadow-lg">
                  <p className="text-xs font-semibold">{PASSI[kind].label}</p>
                  <p className="text-[11px] text-white/70 leading-snug mt-0.5">
                    {PASSI[kind].spiegazione}
                  </p>
                </div>
              </div>
            </div>
          );
        })}
      </div>

      <p className="text-[9.5px] text-neutral-400 text-center leading-tight pt-2 max-w-[64px]">
        Clicca o trascina
      </p>
    </div>
  );
}
